/**
 * The project's files (RFC 0277 S6, S8, S11).
 *
 * RFC 0357 places a source's folder beside document folders in the visible
 * tree. The file listing still comes from this device's mount, not the synced
 * document catalog, and a path cannot hold both kinds of content.
 *
 * IT FOLLOWS THE SESSION (S11). A project has several worktrees on this device,
 * each on one branch, and a session works in one of them. The tree shows the
 * focused session's worktree, and a project the session is not in shows the
 * worktree of its own last active session, remembered on this device. Picking
 * another worktree or branch by hand changes only what this window lists,
 * never where a session works, and lasts until the session changes.
 *
 * LAZY ALL THE WAY DOWN. The section starts closed and lists nothing. Opening it
 * asks the git server for one level of the root; opening a folder asks for one
 * level of that folder. A monorepo with tens of thousands of files costs one
 * directory per click and nothing up front.
 *
 * A BRANCH PICKER THAT MOVES NOTHING (S8). Choosing a ref changes what is
 * listed and what opens, read from the commit through `ls-tree` and `fileAt`.
 * It never checks anything out, so it is safe while an agent is mid-turn in the
 * same checkout. Moving the working tree is S9 and is deliberately absent.
 *
 * The listing cache is keyed by ref AND directory, so flipping back to a branch
 * already read does not ask again. The working tree does change under you, which
 * is what the refresh control is for.
 */

import { ChevronDown, ChevronRight, RotateCw, X } from 'lucide-react'
import {
  type ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { fetchVcsFromServer } from '../../sdk/vcs'
import { codeFileFor, codeFileId } from '../../services/code/codeFile'
import {
  PROJECT_FOLDER_EVENT,
  readProjectFolder,
} from '../../services/code/projectFolder'
import {
  type CodeWorktree,
  listProjectWorktrees,
} from '../../services/code/worktrees'
import type { Subject } from '../../shell/window/subject'
import { FileBadge } from './FileBadge'

export interface CodeEntry {
  name: string
  path: string
  kind: 'dir' | 'file'
}

export interface CodeListing {
  dir: string
  ref: string | null
  entries: CodeEntry[]
}

export interface CodeBranch {
  branch: string
  remote?: boolean
  /** The branch the working tree is on, so the picker can name it. */
  current?: boolean
}

export type ListCodeDir = (
  dir: string,
  ref: string | null,
) => Promise<CodeListing>
export type ListCodeBranches = () => Promise<CodeBranch[]>

export type { CodeWorktree }
export type ListCodeWorktrees = () => Promise<CodeWorktree[]>

const serverWorktrees =
  (projectId: string, sourceId?: string): ListCodeWorktrees =>
  () =>
    listProjectWorktrees(projectId, sourceId)

/** The branch of this project's last active session, per device (S11). */
const FOLLOW_KEY = 'mod_w_files_follow_v1'

const followedKey = (projectId: string, sourceId?: string) =>
  `${FOLLOW_KEY}:${projectId}${sourceId && sourceId !== 'default' ? `:${sourceId}` : ''}`

function readFollowed(projectId: string, sourceId?: string): string | null {
  try {
    return localStorage.getItem(followedKey(projectId, sourceId))
  } catch {
    return null
  }
}

function writeFollowed(
  projectId: string,
  branch: string,
  sourceId?: string,
): void {
  try {
    localStorage.setItem(followedKey(projectId, sourceId), branch)
  } catch {
    // Unavailable storage only loses the memory, not the tree.
  }
}

/**
 * Where the tree reads for a session's branch: the working tree when the
 * main checkout has it out, `~branch` for a sibling worktree, or null when no
 * worktree here has it (the caller then falls back).
 */
export function followRef(
  branch: string | null | undefined,
  trees: ReadonlyArray<CodeWorktree>,
): { ref: string | null } | null {
  if (!branch) return null
  const tree = trees.find((t) => t.branch === branch)
  if (!tree) return null
  return { ref: tree.main ? null : `~${branch}` }
}

/** The project's git, on the daemon's one server (RFC 0331 3a). */
const serverList =
  (projectId: string, sourceId?: string): ListCodeDir =>
  async (dir, ref) =>
    (await fetchVcsFromServer(
      'list',
      [dir, ref ?? ''],
      projectId,
      sourceId,
    )) as CodeListing

const serverBranches =
  (projectId: string, sourceId?: string): ListCodeBranches =>
  async () =>
    (await fetchVcsFromServer(
      'branches',
      [],
      projectId,
      sourceId,
    )) as CodeBranch[]

type DirState =
  | { status: 'loading' }
  | { status: 'error'; message: string }
  | { status: 'ready'; entries: CodeEntry[] }

const cacheKey = (ref: string | null, dir: string) => `${ref ?? ''}\0${dir}`

const CODE_TREE_STATE_KEY = 'mod_code_tree_v1'

interface SavedCodeTreeState {
  open: boolean
  ref: string | null
  expanded: string[]
  /** What the tree last followed, so a remount does not undo a hand pick. */
  followKey?: string
}

const emptySavedState = (): SavedCodeTreeState => ({
  open: false,
  ref: null,
  expanded: [],
})

/**
 * The sidebar is unmounted while it is hidden and mounted again for its hover
 * reveal. This small piece of session state belongs to the window, not to the
 * project or the synced model: it keeps that remount from looking like a new
 * code browser while still letting a new browser session start folded.
 */
function loadSavedState(scope?: string): SavedCodeTreeState {
  if (!scope) return emptySavedState()
  try {
    const raw = sessionStorage.getItem(`${CODE_TREE_STATE_KEY}:${scope}`)
    if (!raw) return emptySavedState()
    const parsed = JSON.parse(raw) as Partial<SavedCodeTreeState>
    return {
      open: parsed.open === true,
      ref: typeof parsed.ref === 'string' ? parsed.ref : null,
      expanded: Array.isArray(parsed.expanded)
        ? parsed.expanded.filter(
            (path): path is string => typeof path === 'string',
          )
        : [],
      ...(typeof parsed.followKey === 'string'
        ? { followKey: parsed.followKey }
        : {}),
    }
  } catch {
    return emptySavedState()
  }
}

function saveState(scope: string | undefined, state: SavedCodeTreeState): void {
  if (!scope) return
  try {
    sessionStorage.setItem(
      `${CODE_TREE_STATE_KEY}:${scope}`,
      JSON.stringify(state),
    )
  } catch {
    // Storage can be unavailable (private/embedded contexts). The tree still
    // works; only the state falls back to the component's lifetime.
  }
}

export type CodeTreeSectionProps = {
  projectId: string
  sources?: Array<{
    id: string
    name: string
    kind: 'git' | 'folder'
    folder?: string
  }>
  /** Render one placed source at its folder, without a surrounding Files row. */
  singleSourceRow?: boolean
  /** The visible folder name, which can differ from the source's catalog name. */
  folderLabel?: string
  defaultSourceId?: string
  /** An old default source with no shared VCS record uses its local mount kind. */
  legacyInferredDefault?: boolean
  onAddSource?: () => void
  /** The source folder participates in the same drag gesture as every other folder. */
  onDragSource?: (sourceId: string, dragging: boolean) => void
  /** RFC 0357 S16: a file row drags like a document row. Called on drag start
   *  and, with false, on drag end. `ref` is what the tree lists: null for the
   *  working tree, `~branch` for a sibling worktree, else a commit. */
  onDragFile?: (
    file: { sourceId: string; path: string; ref: string | null },
    dragging: boolean,
  ) => void
  /** RFC 0357 S16: the drop target for a folder inside the checkout (`dir`
   *  '' is the source's root). The directory decides what a drop there does. */
  dropIn?: (target: CodeDropTarget) => CodeMoveTarget | undefined
  /** The directory owns placed source rows; this section supplies only their controls and file children. */
  renderSourceRow?: (props: {
    sourceId: string
    icon: ReactNode
    label: string
    title: string
    depth: number
    trailing: ReactNode
    /** The row's menu: add, mount, remove. The directory draws it as the
     *  row's three dots, so a source row spends no room on rare actions. */
    actions: Array<{
      label: string
      onSelect: () => void
      danger?: boolean
      confirm?: string
    }>
    onClick: () => void
    moveTarget?: CodeMoveTarget
  }) => ReactNode
  onMountSource?: (sourceId: string) => void
  onRemoveSource?: (sourceId: string) => void
  stateScope?: string
  openFileId?: string | null
  onOpen?: (file: Subject) => void
  onOpenSplit?: (file: Subject) => void
  list?: ListCodeDir
  branches?: ListCodeBranches
  worktrees?: ListCodeWorktrees
  followBranch?: string | null
  followSourceId?: string | null
  embedded?: boolean
  folderOpen?: boolean
  onToggleFolder?: () => void
  depth?: number
  Row: (props: {
    icon: ReactNode
    label: string
    title?: string
    active?: boolean
    depth?: number
    tone?: 'project' | 'folder'
    trailing?: ReactNode
    onClick?: () => void
    onSplit?: () => void
    moveDrag?: (dragging: boolean) => void
    moveTarget?: CodeMoveTarget
  }) => ReactNode
}

/** A folder inside a checkout, as a drop target (RFC 0357 S16). */
export interface CodeDropTarget {
  sourceId: string
  /** Repo-relative; '' is the source's root. */
  dir: string
  ref: string | null
  /** Whether this device has the checkout at all. */
  mounted: boolean
}

/** What the directory's rows take: the sidebar's own move-target contract. */
export interface CodeMoveTarget {
  canDrop: () => boolean
  refusal?: () => string | null
  onDrop: () => void
}

/**
 * Fired on the window when a cross move changed a checkout on disk (RFC 0357
 * S16), with `{ projectId, sourceId }`, so the tree lists it again. The
 * daemon tells nobody about a file it wrote at the window's own request.
 */
export const CODE_TREE_CHANGED_EVENT = 'mod:code-tree-changed'

export function CodeTreeSection(props: CodeTreeSectionProps) {
  const {
    embedded,
    sources = [],
    folderOpen = true,
    onToggleFolder,
    onAddSource,
    Row,
    depth = 0,
  } = props
  if (props.singleSourceRow) {
    return <SingleCodeTreeSection {...props} sourceRow />
  }
  if (embedded && sources.length > 0) {
    return (
      <>
        <Row
          icon={
            folderOpen ? <ChevronDown size={13} /> : <ChevronRight size={13} />
          }
          label="Files"
          tone="folder"
          depth={depth}
          onClick={onToggleFolder}
          trailing={
            folderOpen && onAddSource ? (
              <button
                type="button"
                aria-label="Add source"
                title="Add a repository or folder"
                onClick={(event) => {
                  event.stopPropagation()
                  onAddSource()
                }}
                className="rounded px-1 text-muted-foreground hover:text-foreground"
              >
                +
              </button>
            ) : null
          }
        />
        {folderOpen &&
          sources.map((source) => (
            <SingleCodeTreeSection
              key={source.id}
              {...props}
              sources={[source]}
              stateScope={`${props.stateScope ?? props.projectId}:${source.id}`}
              depth={depth + 1}
              sourceRow
              onAddSource={undefined}
              followBranch={
                source.id === (props.followSourceId ?? props.defaultSourceId)
                  ? props.followBranch
                  : null
              }
            />
          ))}
      </>
    )
  }
  return <SingleCodeTreeSection {...props} />
}

function SingleCodeTreeSection({
  projectId,
  sources = [],
  folderLabel,
  defaultSourceId = 'default',
  legacyInferredDefault = false,
  onAddSource,
  onDragSource,
  onDragFile,
  dropIn,
  renderSourceRow,
  onMountSource,
  onRemoveSource,
  stateScope,
  openFileId,
  onOpen,
  onOpenSplit,
  list: listOverride,
  branches: branchesOverride,
  worktrees: worktreesOverride,
  followBranch,
  embedded = false,
  folderOpen = true,
  onToggleFolder,
  depth: baseDepth = 0,
  Row,
  sourceRow = false,
}: CodeTreeSectionProps & { sourceRow?: boolean }) {
  const [sourceId, setSourceId] = useState(
    sourceRow ? (sources[0]?.id ?? defaultSourceId) : defaultSourceId,
  )
  const selected = sources.find((s) => s.id === sourceId)
  useEffect(() => {
    if (sources.length && !selected) setSourceId(defaultSourceId)
  }, [sources, selected, defaultSourceId])
  const [mounted, setMounted] = useState<boolean | null>(null)
  const [mountedGit, setMountedGit] = useState(false)
  /** Where this device has the source, for naming an inferred one by it. */
  const [mountPath, setMountPath] = useState<string | null>(null)
  useEffect(() => {
    if (!sources.length) return
    let live = true
    const read = () => {
      void readProjectFolder(projectId, sourceId).then((f) => {
        if (live) {
          setMounted(!!f?.path)
          setMountedGit(f?.git === true)
          setMountPath(f?.path ?? null)
        }
      })
    }
    read()
    window.addEventListener(PROJECT_FOLDER_EVENT, read)
    return () => {
      live = false
      window.removeEventListener(PROJECT_FOLDER_EVENT, read)
    }
  }, [projectId, sourceId, sources.length])
  const selectedKind =
    legacyInferredDefault && sourceId === defaultSourceId && mountedGit
      ? 'git'
      : selected?.kind
  const list = useMemo(
    () => listOverride ?? serverList(projectId, sourceId),
    [listOverride, projectId, sourceId],
  )
  const branches = useMemo(
    () => branchesOverride ?? serverBranches(projectId, sourceId),
    [branchesOverride, projectId, sourceId],
  )
  const worktrees = useMemo(
    () => worktreesOverride ?? serverWorktrees(projectId, sourceId),
    [worktreesOverride, projectId, sourceId],
  )
  const saved = useMemo(() => loadSavedState(stateScope), [stateScope])
  const [ownOpen, setOwnOpen] = useState(saved.open)
  const open =
    embedded && (!sourceRow || renderSourceRow) ? folderOpen : ownOpen
  const [ref, setRef] = useState<string | null>(saved.ref)
  const [expanded, setExpanded] = useState<Set<string>>(
    () => new Set(saved.expanded),
  )
  const [dirs, setDirs] = useState<Map<string, DirState>>(() => new Map())
  const [refs, setRefs] = useState<CodeBranch[] | null>(null)
  const [trees, setTrees] = useState<CodeWorktree[] | null>(null)
  const readGeneration = useRef(0)
  useEffect(
    () => () => {
      readGeneration.current++
    },
    [],
  )
  const chooseSource = (id: string) => {
    readGeneration.current++
    setSourceId(id)
    setMounted(null)
    setDirs(new Map())
    setRefs(null)
    setTrees(null)
    setRef(null)
    setExpanded(new Set())
  }

  const load = useCallback(
    (dir: string, at: string | null, attempt = 0) => {
      const generation = readGeneration.current
      const key = cacheKey(at, dir)
      setDirs((prev) => new Map(prev).set(key, { status: 'loading' }))
      list(dir, at).then(
        (listing) => {
          if (generation !== readGeneration.current) return
          setDirs((prev) =>
            new Map(prev).set(key, {
              status: 'ready',
              entries: listing.entries,
            }),
          )
        },
        (e: unknown) => {
          if (generation !== readGeneration.current) return
          // Unreachable, not refused: the daemon restarts its servers after
          // a mount or a move. Ask again rather than show "Failed to fetch".
          if (e instanceof TypeError && attempt < 6) {
            setTimeout(
              () => {
                if (generation === readGeneration.current)
                  load(dir, at, attempt + 1)
              },
              500 * 2 ** attempt,
            )
            return
          }
          setDirs((prev) =>
            new Map(prev).set(key, {
              status: 'error',
              message: e instanceof Error ? e.message : String(e),
            }),
          )
        },
      )
    },
    [list],
  )

  // The root, and any folders restored after the hidden sidebar remounts. A
  // fresh section still asks only for the root; restored folders were already
  // explicitly opened by the person, so re-listing them restores that view.
  useEffect(() => {
    if (!open || (sources.length > 0 && mounted !== true)) return
    for (const dir of ['', ...expanded]) {
      if (!dirs.has(cacheKey(ref, dir))) load(dir, ref)
    }
  }, [open, ref, expanded, dirs, load, mounted, sources.length])

  useEffect(() => {
    saveState(stateScope, {
      open,
      ref,
      expanded: [...expanded],
      ...(lastFollowKey.current ? { followKey: lastFollowKey.current } : {}),
    })
  }, [stateScope, open, ref, expanded])

  // Branches, once, when the section is first opened, or at once for a source
  // row whose folder is here, since its picker shows before it is expanded.
  // The closed read goes through a short shared cache: the sidebar remounts
  // on every reveal, and two git calls per source row each time added up
  // (review of PR 364, 4). A closed read that fails tries again on the next
  // mount rather than leaving the picker empty; an open one shows the
  // working tree alone, as before.
  const eager = !open && !!sourceRow && mounted === true
  const wantRefs = open || eager
  const sourceKey = `${projectId}\u0000${sourceId}`
  useEffect(() => {
    if (!wantRefs || refs !== null || selectedKind === 'folder') return
    const generation = readGeneration.current
    const read =
      eager && !branchesOverride
        ? cachedRead(`branches\u0000${sourceKey}`, branches)
        : branches()
    read.then(
      (b) => {
        if (generation === readGeneration.current) setRefs(b)
      },
      () => {
        if (generation === readGeneration.current && !eager) setRefs([])
      },
    )
  }, [
    wantRefs,
    eager,
    refs,
    branches,
    branchesOverride,
    selectedKind,
    sourceKey,
  ])

  // The worktrees, the same way. Failing leaves the picker as it was before
  // S11 rather than hiding the section.
  useEffect(() => {
    if (!wantRefs || trees !== null || selectedKind === 'folder') return
    const generation = readGeneration.current
    const read =
      eager && !worktreesOverride
        ? cachedRead(`worktrees\u0000${sourceKey}`, worktrees)
        : worktrees()
    read.then(
      (t) => {
        if (generation === readGeneration.current) setTrees(t)
      },
      () => {
        if (generation === readGeneration.current && !eager) setTrees([])
      },
    )
  }, [
    wantRefs,
    eager,
    trees,
    worktrees,
    worktreesOverride,
    selectedKind,
    sourceKey,
  ])

  // FOLLOW THE SESSION. Re-applied only when the session's branch or the set of
  // worktrees changes, so a hand-picked ref lasts until the person moves to
  // another session. A session with no worktree here falls back to this
  // project's last followed branch on this device, then to the main checkout.
  const followKey = `${followBranch ?? ''}\0${(trees ?? []).map((t) => t.branch).join(',')}`
  const lastFollowKey = useRef<string | null>(saved.followKey ?? null)
  /** The session a hand pick was made under. The pick stands until the
   *  session changes, even if the worktree list arrives after it. */
  const pickedUnder = useRef<string | null>(null)
  useEffect(() => {
    if (trees === null || lastFollowKey.current === followKey) return
    lastFollowKey.current = followKey
    if (pickedUnder.current === (followBranch ?? '')) return
    pickedUnder.current = null
    const own = followRef(followBranch, trees)
    if (own && followBranch) writeFollowed(projectId, followBranch, sourceId)
    const next = own ?? followRef(readFollowed(projectId, sourceId), trees)
    setRef(next ? next.ref : null)
  }, [followKey, followBranch, trees, projectId, sourceId])

  const toggleDir = (path: string) => {
    const next = new Set(expanded)
    if (!next.delete(path)) {
      next.add(path)
      if (!dirs.has(cacheKey(ref, path))) load(path, ref)
    }
    setExpanded(next)
  }

  const chooseRef = (next: string | null) => {
    pickedUnder.current = followBranch ?? ''
    setRef(next)
    // Folders that were open stay open at the new ref, so a person comparing
    // one directory across branches does not have to walk down to it again.
    for (const path of expanded) {
      if (!dirs.has(cacheKey(next, path))) load(path, next)
    }
  }

  const refresh = () => {
    readGeneration.current++
    setDirs(new Map())
    setRefs(null)
    setTrees(null)
    for (const path of expanded) load(path, ref)
  }

  // A cross move wrote or removed a file here at this window's request;
  // nothing else says so, so list again what is open.
  useEffect(() => {
    const onChanged = (event: Event) => {
      const detail = (event as CustomEvent).detail as
        | { projectId?: string; sourceId?: string }
        | undefined
      if (detail?.projectId !== projectId || detail.sourceId !== sourceId)
        return
      readGeneration.current++
      setDirs(new Map())
    }
    window.addEventListener(CODE_TREE_CHANGED_EVENT, onChanged)
    return () => window.removeEventListener(CODE_TREE_CHANGED_EVENT, onChanged)
  }, [projectId, sourceId])

  const parentDir = (path: string) => path.split('/').slice(0, -1).join('/')
  /** The drop target for a folder in this checkout, when the directory takes one. */
  const targetFor = (dir: string): CodeMoveTarget | undefined =>
    dropIn?.({ sourceId, dir, ref, mounted: mounted === true })

  const render = (dir: string, depth: number): ReactNode[] => {
    const state = dirs.get(cacheKey(ref, dir))
    if (!state || state.status === 'loading') {
      return [<Note key={`loading:${dir}`} depth={depth} text="Listing…" />]
    }
    if (state.status === 'error') {
      return [
        <Note
          key={`error:${dir}`}
          depth={depth}
          text={
            dir === '' ? `Source unavailable: ${state.message}` : state.message
          }
        />,
      ]
    }
    if (state.entries.length === 0 && dir === '') {
      return [<Note key="empty" depth={depth} text="No files at this ref." />]
    }
    return state.entries.flatMap((entry) => {
      if (entry.kind === 'file') {
        const file = codeFileFor(
          entry.path,
          ref,
          sources.length ? sourceId : undefined,
        )
        return [
          <Row
            key={`file:${entry.path}`}
            icon={<FileBadge name={entry.name} />}
            label={entry.name}
            title={ref ? `${entry.path} @ ${ref}` : entry.path}
            depth={depth}
            active={
              codeFileId(
                entry.path,
                ref,
                sources.length ? sourceId : undefined,
              ) === openFileId
            }
            onClick={() => onOpen?.(file)}
            onSplit={onOpenSplit ? () => onOpenSplit(file) : undefined}
            moveDrag={
              onDragFile
                ? (dragging) =>
                    onDragFile({ sourceId, path: entry.path, ref }, dragging)
                : undefined
            }
            // A drop on a file means its folder, as it does for a document.
            moveTarget={targetFor(parentDir(entry.path))}
          />,
        ]
      }
      const isOpen = expanded.has(entry.path)
      return [
        <Row
          key={`dir:${entry.path}`}
          icon={isOpen ? <ChevronDown size={13} /> : <ChevronRight size={13} />}
          label={entry.name}
          title={entry.path}
          depth={depth}
          onClick={() => toggleDir(entry.path)}
          moveTarget={targetFor(entry.path)}
        />,
        ...(isOpen ? render(entry.path, depth + 1) : []),
      ]
    })
  }

  const picker = (
    <span className="flex shrink-0 items-center gap-1 pr-1">
      {sources.length > 0 && !sourceRow && (
        <select
          aria-label="Source"
          value={sourceId}
          onClick={(e) => e.stopPropagation()}
          onChange={(e) => chooseSource(e.target.value)}
          className="max-w-[7rem] rounded border border-border/60 bg-background text-[11px]"
        >
          {sources.map((s) => (
            <option key={s.id} value={s.id}>
              {s.name}
            </option>
          ))}
        </select>
      )}
      {/* A source row has its own add button; a second here was two "+". */}
      {onAddSource && !sourceRow && (
        <button
          type="button"
          aria-label="Add source"
          title="Add a repository or folder"
          onClick={(e) => {
            e.stopPropagation()
            onAddSource()
          }}
          className="rounded px-1 text-muted-foreground hover:text-foreground"
        >
          +
        </button>
      )}
      {mounted === false && onMountSource && !sourceRow && (
        <button
          type="button"
          aria-label="Mount source here"
          title="Choose this source's folder on this device"
          onClick={(e) => {
            e.stopPropagation()
            onMountSource(sourceId)
          }}
          className="rounded px-1 text-[11px] text-muted-foreground hover:text-foreground"
        >
          Mount
        </button>
      )}
      {sourceId !== defaultSourceId && onRemoveSource && !sourceRow && (
        <button
          type="button"
          aria-label="Remove source"
          title="Remove this source from the project"
          onClick={(e) => {
            e.stopPropagation()
            onRemoveSource(sourceId)
          }}
          className="rounded px-1 text-[11px] text-muted-foreground hover:text-foreground"
        >
          ×
        </button>
      )}
      {selectedKind !== 'folder' && (
        <CodeRefSelect
          value={ref}
          onChange={chooseRef}
          refs={refs}
          trees={trees}
          className="max-w-[9rem] truncate rounded border border-border/60 bg-background px-1 py-0.5 text-[11px] text-muted-foreground"
        />
      )}
      <button
        type="button"
        aria-label="Refresh code listing"
        title="List again"
        onClick={refresh}
        className="rounded p-0.5 text-muted-foreground hover:bg-muted hover:text-foreground"
      >
        <RotateCw size={11} />
      </button>
    </span>
  )

  // A source can be removed while its tree is closed. Keep the action on the
  // source row rather than hiding it with the branch picker inside the tree.
  const sourceRowActions = sourceRow ? (
    <span className="flex shrink-0 items-center gap-1 pr-1">
      {sourceId === defaultSourceId && onAddSource && (
        <button
          type="button"
          aria-label="Add source"
          title="Add a repository or folder"
          onClick={(e) => {
            e.stopPropagation()
            onAddSource()
          }}
          className="rounded px-1 text-muted-foreground hover:text-foreground"
        >
          +
        </button>
      )}
      {mounted === false && onMountSource && (
        <button
          type="button"
          aria-label={`Mount ${selected?.name ?? sourceId} here`}
          title="Choose this source's folder on this device"
          onClick={(e) => {
            e.stopPropagation()
            onMountSource(sourceId)
          }}
          className="rounded px-1 text-[11px] text-muted-foreground hover:text-foreground"
        >
          Mount
        </button>
      )}
      {sourceId !== defaultSourceId && onRemoveSource && (
        <button
          type="button"
          aria-label={`Remove ${selected?.name ?? sourceId} from project`}
          title="Remove from this project; files stay on disk"
          onClick={(e) => {
            e.stopPropagation()
            onRemoveSource(sourceId)
          }}
          className="rounded p-0.5 text-muted-foreground hover:bg-muted hover:text-foreground"
        >
          <X size={12} />
        </button>
      )}
      {open && picker}
    </span>
  ) : null

  // The same actions as a menu, for a directory that draws the row (RFC 0357
  // paper cuts): the row keeps only its branch, so a name has room.
  const sourceName = selected?.name ?? sourceId
  const sourceMenu = [
    ...(sourceId === defaultSourceId && onAddSource
      ? [{ label: 'Add repository or folder', onSelect: onAddSource }]
      : []),
    ...(mounted === false && onMountSource
      ? [
          {
            label: `Mount ${sourceName} here`,
            onSelect: () => onMountSource(sourceId),
          },
        ]
      : []),
    ...(sourceId !== defaultSourceId && onRemoveSource
      ? [
          {
            label: 'Remove from project',
            onSelect: () => onRemoveSource(sourceId),
            danger: true,
            confirm: `Remove ${sourceName}? Files stay on disk`,
          },
        ]
      : []),
  ]
  const sourceRowBranch =
    sourceRow && mounted !== false ? (
      // Picking a branch is not a click on the row.
      // biome-ignore lint/a11y/noStaticElementInteractions: only stops the click reaching the row
      // biome-ignore lint/a11y/useKeyWithClickEvents: the select inside handles its own keys
      <span
        className="flex shrink-0 items-center gap-1 pr-1"
        onClick={(e) => e.stopPropagation()}
      >
        {selectedKind !== 'folder' && (
          <CodeRefSelect
            value={ref}
            onChange={chooseRef}
            refs={refs}
            trees={trees}
            className="max-w-[7rem] truncate rounded border border-border/60 bg-background px-1 py-0.5 text-[11px] text-muted-foreground"
          />
        )}
        {open && (
          <button
            type="button"
            aria-label="Refresh code listing"
            title="List again"
            onClick={(e) => {
              e.stopPropagation()
              refresh()
            }}
            className="rounded p-0.5 text-muted-foreground hover:bg-muted hover:text-foreground"
          >
            <RotateCw size={11} />
          </button>
        )}
      </span>
    ) : null

  // RFC 0357 3d: an older project's assumed "Project files" source is a guess,
  // not something anyone attached. It shows only where this device has it,
  // named after that folder; elsewhere it is not a row of its own.
  const inferred =
    legacyInferredDefault && sourceRow && sourceId === defaultSourceId
  if (inferred && mounted === false) return null
  const inferredName =
    inferred && mountPath
      ? mountPath.replace(/\/+$/, '').split('/').pop() || undefined
      : undefined
  // The folder name wins over the tree's placeholder "Project files".
  const label = inferredName ?? folderLabel

  if (embedded)
    return (
      <>
        {sourceRow && renderSourceRow ? (
          renderSourceRow({
            sourceId,
            icon: open ? <ChevronDown size={13} /> : <ChevronRight size={13} />,
            label: label ?? selected?.name ?? sourceId,
            title:
              mounted === false
                ? 'Source unavailable on this device'
                : `Files in ${label ?? selected?.name ?? sourceId}`,
            depth: baseDepth,
            trailing: sourceRowBranch,
            actions: sourceMenu,
            onClick: onToggleFolder ?? (() => {}),
            moveTarget: targetFor(''),
          })
        ) : (
          <Row
            icon={open ? <ChevronDown size={13} /> : <ChevronRight size={13} />}
            label={sourceRow ? (label ?? selected?.name ?? sourceId) : 'Files'}
            tone="folder"
            title={
              mounted === false
                ? 'Source unavailable on this device'
                : sourceRow
                  ? `Files in ${label ?? selected?.name ?? sourceId}`
                  : 'The project source files'
            }
            depth={baseDepth}
            trailing={sourceRow ? sourceRowActions : open ? picker : null}
            onClick={
              sourceRow ? () => setOwnOpen((value) => !value) : onToggleFolder
            }
            moveDrag={
              sourceRow && onDragSource
                ? (dragging) => onDragSource(sourceId, dragging)
                : undefined
            }
            moveTarget={sourceRow ? targetFor('') : undefined}
          />
        )}
        {open &&
          (mounted === false ? (
            <Note
              depth={baseDepth + 1}
              text="Unavailable on this device. Mount this source here."
            />
          ) : (
            render('', baseDepth + 1)
          ))}
      </>
    )

  return (
    <div className="mt-2 border-border/60 border-t pt-1">
      <div className="flex items-center gap-1 pr-1">
        <button
          type="button"
          aria-expanded={open}
          onClick={() => setOwnOpen((v) => !v)}
          className="flex min-w-0 flex-1 items-center gap-1 px-2 pt-1 pb-0.5 text-left text-[11px] text-muted-foreground/60 hover:text-muted-foreground"
          title="The project's files from git, in the session's worktree"
        >
          {open ? <ChevronDown size={11} /> : <ChevronRight size={11} />}
          <span className="truncate">Files</span>
        </button>
        {open && picker}
      </div>
      {open &&
        (mounted === false ? (
          <Note
            depth={baseDepth}
            text="Unavailable on this device. Mount this source here."
          />
        ) : (
          render('', baseDepth)
        ))}
    </div>
  )
}

/**
 * Which ref a file list reads: the working tree, a sibling worktree, a local
 * or a remote branch. Shared by the sidebar's Files and a code pane's Files
 * column (RFC 0354 3a). Picking one changes what is listed, never a checkout.
 */
/** Closed source rows' branch reads, shared for half a minute across
 *  remounts (review of PR 364, 4). A failed read is dropped at once. */
const readCache = new Map<string, { at: number; read: Promise<unknown> }>()
function cachedRead<T>(key: string, read: () => Promise<T>): Promise<T> {
  const hit = readCache.get(key)
  if (hit && Date.now() - hit.at < 30_000) return hit.read as Promise<T>
  const next = read()
  readCache.set(key, { at: Date.now(), read: next })
  next.catch(() => readCache.delete(key))
  return next
}

export function CodeRefSelect({
  value,
  onChange,
  refs,
  trees,
  className,
}: {
  value: string | null
  onChange: (ref: string | null) => void
  refs: ReadonlyArray<CodeBranch> | null
  trees: ReadonlyArray<CodeWorktree> | null
  className?: string
}) {
  const local = (refs ?? []).filter((b) => !b.remote)
  const remote = (refs ?? []).filter((b) => b.remote)
  const current = (refs ?? []).find((b) => b.current)?.branch
  const siblings = (trees ?? []).filter((t) => !t.main && t.branch)
  // A ref that is in none of the lists (a sha, a branch not fetched yet) is
  // still what is shown, so it gets an option rather than a blank select.
  const known = new Set([
    '',
    ...siblings.map((t) => `~${t.branch}`),
    ...(refs ?? []).map((b) => b.branch),
  ])
  return (
    <select
      aria-label="Show the files of"
      value={value ?? ''}
      onChange={(e) => onChange(e.target.value || null)}
      className={className}
      title="Changes what this window lists, never where a session works"
    >
      {/* The working tree IS a branch's, and saying which one is what
        tells a reader whether "working tree" and "feat/x" are the
        same files or not. Bare when the daemon has not said yet. */}
      <option value="">{current ? `wt (${current})` : 'Working tree'}</option>
      {value && !known.has(value) && (
        <option value={value}>
          {value.startsWith('~')
            ? `${value.slice(1)} (worktree unavailable)`
            : value}
        </option>
      )}
      {siblings.length > 0 && (
        <optgroup label="Worktrees">
          {siblings.map((t) => (
            <option key={t.name} value={`~${t.branch}`}>
              {t.name} ({t.branch})
            </option>
          ))}
        </optgroup>
      )}
      {local.length > 0 && (
        <optgroup label="Branches">
          {local.map((b) => (
            <option key={b.branch} value={b.branch}>
              {b.branch}
            </option>
          ))}
        </optgroup>
      )}
      {remote.length > 0 && (
        <optgroup label="Remote">
          {remote.map((b) => (
            <option key={b.branch} value={b.branch}>
              {b.branch}
            </option>
          ))}
        </optgroup>
      )}
    </select>
  )
}

function Note({ text, depth }: { text: string; depth: number }) {
  return (
    <p
      className="py-0.5 pr-2 text-[11px] text-muted-foreground/60"
      style={{ paddingLeft: 8 + depth * 12 }}
    >
      {text}
    </p>
  )
}
